import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form, Card, Radio, Upload, Checkbox, Input, Slider, Button,
  Space, Typography, Row, Col, Tag, message, Select, Switch, Divider,
} from 'antd';
import {
  UploadOutlined, EyeOutlined, SaveOutlined, DesktopOutlined,
  EnvironmentOutlined, ArrowUpOutlined, ArrowDownOutlined,
  ClockCircleOutlined, BgColorsOutlined, PictureOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const PRIMARY     = '#2563eb';
const DARK        = '#1e293b';
const STORAGE_KEY = 'welcome_screen_config';

const MinimalDarkPreview = () => (
  <div style={{
    width: '100%', height: 90, borderRadius: 6,
    background: '#0f172a', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: 4,
  }}>
    <div style={{ width: 70, height: 10, background: '#fff', borderRadius: 3 }} />
    <div style={{ width: 45, height: 6, background: '#94a3b8', borderRadius: 3 }} />
  </div>
);

const ModernCardPreview = () => (
  <div style={{
    width: '100%', height: 90, borderRadius: 6,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <div style={{
      background: '#fff', borderRadius: 6, padding: '6px 14px',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    }}>
      <div style={{ width: 60, height: 8, background: '#1e293b', borderRadius: 3 }} />
      <div style={{ width: 40, height: 5, background: '#94a3b8', borderRadius: 3 }} />
    </div>
  </div>
);

const BrandedPreview = () => (
  <div style={{
    width: '100%', height: 90, borderRadius: 6,
    background: 'linear-gradient(135deg, #f97316 0%, #ec4899 100%)',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: 5,
  }}>
    <div style={{
      width: 28, height: 28, borderRadius: '50%',
      background: 'rgba(255,255,255,0.35)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{ width: 14, height: 14, background: '#fff', borderRadius: 3 }} />
    </div>
    <div style={{ width: 55, height: 7, background: '#fff', borderRadius: 3 }} />
  </div>
);

const TEMPLATES = [
  { key: 'minimal_dark',  label: 'Minimal Dark',         desc: 'Dark background, large name text',          Preview: MinimalDarkPreview },
  { key: 'modern_card',   label: 'Modern Card',           desc: 'White card on gradient background',         Preview: ModernCardPreview },
  { key: 'full_branded',  label: 'Full Screen Branded',   desc: 'Company logo prominent, vibrant colors',    Preview: BrandedPreview },
];

const FIELD_LABELS = {
  name:           'Delegate Name',
  category:       'Category',
  company:        'Company',
  location:       'Location',
  welcomeMessage: 'Welcome Message',
  photo:          'Photo',
};

const ALL_FIELDS = ['photo', 'name', 'category', 'company', 'location', 'welcomeMessage'];

const CLOCK_POSITIONS = [
  { value: 'top-left',      label: '↖ Top Left' },
  { value: 'top-center',    label: '↑ Top Center' },
  { value: 'top-right',     label: '↗ Top Right' },
  { value: 'bottom-left',   label: '↙ Bottom Left' },
  { value: 'bottom-center', label: '↓ Bottom Center' },
  { value: 'bottom-right',  label: '↘ Bottom Right' },
];

const DEFAULT_CONFIG = {
  technology:      'uhf',
  template:        'minimal_dark',
  logoUrl:         null,
  logoPlacement:   'center',
  fields:          ['name', 'category', 'company', 'welcomeMessage'],
  fieldOrder:      ['photo', 'name', 'category', 'company', 'location', 'welcomeMessage'],
  welcomeMessage:  'Welcome to the Event!',
  displayDuration: 8,
  // Background
  bgType:          'gradient',   // 'gradient' | 'solid' | 'image'
  bgColor:         '#0f172a',
  bgImageUrl:      null,
  // Clock
  showClock:       false,
  clockPosition:   'top-right',
  clockFormat:     '24h',        // '24h' | '12h'
};

export default function WelcomeScreenConfig() {
  const navigate = useNavigate();
  const [config, setConfig]       = useState(DEFAULT_CONFIG);
  const [logoFile, setLogoFile]   = useState(null);
  const [bgImgFile, setBgImgFile] = useState(null);
  const [events, setEvents]       = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setConfig(prev => ({ ...prev, ...JSON.parse(saved) }));
    } catch { /* ignore */ }
  }, []);

  // Load events on mount
  useEffect(() => {
    axios.get('/api/welcome/events')
      .then(r => setEvents(r.data.data || []))
      .catch(() => {});
  }, []);

  // Load locations when event changes
  useEffect(() => {
    if (!config.event_id) { setLocations([]); return; }
    axios.get('/api/welcome/locations', { params: { event_id: config.event_id } })
      .then(r => setLocations(r.data.data || []))
      .catch(() => {});
  }, [config.event_id]);

  const updateConfig = (partial) => setConfig(prev => ({ ...prev, ...partial }));

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleLogoUpload = async (file) => {
    try {
      const dataUrl = await toBase64(file);
      setLogoFile(file);
      updateConfig({ logoUrl: dataUrl });
    } catch {
      message.error('Failed to load logo image.');
    }
    return false;
  };

  const handleBgImageUpload = async (file) => {
    try {
      const dataUrl = await toBase64(file);
      setBgImgFile(file);
      updateConfig({ bgImageUrl: dataUrl, bgType: 'image' });
    } catch {
      message.error('Failed to load background image.');
    }
    return false;
  };

  const handleSave = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
      message.success('Configuration saved!');
    } catch {
      message.error('Failed to save configuration.');
    }
  };

  // Field order helpers
  const moveField = (index, dir) => {
    const order = [...(config.fieldOrder || ALL_FIELDS)];
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= order.length) return;
    [order[index], order[newIdx]] = [order[newIdx], order[index]];
    updateConfig({ fieldOrder: order });
  };

  const SectionHeader = ({ title, icon }) => (
    <Space style={{ marginBottom: 4 }}>
      <span style={{ fontSize: 16, color: PRIMARY }}>{icon}</span>
      <Text strong style={{ fontSize: 15, color: DARK }}>{title}</Text>
    </Space>
  );

  const fieldOrder = config.fieldOrder || ALL_FIELDS;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space align="center" size={10}>
          <DesktopOutlined style={{ fontSize: 26, color: PRIMARY }} />
          <Title level={4} style={{ margin: 0, color: DARK }}>Welcome Screen Configuration</Title>
        </Space>
        <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
          Configure how the welcome screen looks when a delegate scans their RFID tag.
        </Text>
      </div>

      <Form layout="vertical">
        <Row gutter={[20, 20]}>

          {/* Technology */}
          <Col xs={24} lg={12}>
            <Card title={<SectionHeader title="Technology Settings" icon="📡" />} style={{ borderRadius: 10 }}>
              <Form.Item label="RFID Technology Mode">
                <Radio.Group
                  value={config.technology}
                  onChange={e => updateConfig({ technology: e.target.value })}
                  buttonStyle="solid"
                >
                  <Radio.Button value="uhf">UHF / Active RFID</Radio.Button>
                  <Radio.Button value="nfc">NFC</Radio.Button>
                </Radio.Group>
              </Form.Item>
              {config.technology === 'uhf' && (
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '8px 12px' }}>
                  <Text style={{ fontSize: 12, color: '#1d4ed8' }}>
                    UHF/Active mode polls the server every 2 seconds for the latest scan.
                  </Text>
                </div>
              )}
              {config.technology === 'nfc' && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '8px 12px' }}>
                  <Text style={{ fontSize: 12, color: '#16a34a' }}>
                    NFC mode listens for wristband taps via a hidden input field on the display screen.
                  </Text>
                </div>
              )}
            </Card>
          </Col>

          {/* Reader Location (default) */}
          <Col xs={24} lg={12}>
            <Card
              title={<SectionHeader title="Default Reader Location" icon={<EnvironmentOutlined />} />}
              style={{ borderRadius: 10 }}
            >
              <Form.Item label="Event" extra="Default event for new display screens.">
                <Select
                  placeholder="All Events"
                  allowClear
                  value={config.event_id || undefined}
                  onChange={val => updateConfig({ event_id: val || null, location_id: null })}
                  options={events.map(e => ({ label: e.event_name, value: e.id }))}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="Location" extra="Each display screen can override this on the display itself." style={{ marginBottom: 0 }}>
                <Select
                  placeholder={config.event_id ? 'All Locations' : 'Select event first'}
                  allowClear
                  disabled={!config.event_id}
                  value={config.location_id || undefined}
                  onChange={val => updateConfig({ location_id: val || null })}
                  options={locations.map(l => ({ label: l.location_name, value: l.id }))}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <div style={{ marginTop: 10, background: '#fafafa', border: '1px solid #e2e8f0', borderRadius: 6, padding: '7px 12px' }}>
                <Text style={{ fontSize: 11, color: '#64748b' }}>
                  Each TV/laptop can select its own location from the display screen.
                </Text>
              </div>
            </Card>
          </Col>

          {/* Background */}
          <Col xs={24} lg={12}>
            <Card title={<SectionHeader title="Background" icon={<BgColorsOutlined />} />} style={{ borderRadius: 10 }}>
              <Form.Item label="Background Type">
                <Radio.Group
                  value={config.bgType}
                  onChange={e => updateConfig({ bgType: e.target.value })}
                  buttonStyle="solid"
                >
                  <Radio.Button value="gradient">Animated Gradient</Radio.Button>
                  <Radio.Button value="solid">Solid Color</Radio.Button>
                  <Radio.Button value="image">Image</Radio.Button>
                </Radio.Group>
              </Form.Item>

              {config.bgType === 'solid' && (
                <Form.Item label="Background Color">
                  <Space align="center">
                    <input
                      type="color"
                      value={config.bgColor || '#0f172a'}
                      onChange={e => updateConfig({ bgColor: e.target.value })}
                      style={{ width: 48, height: 36, border: 'none', borderRadius: 4, cursor: 'pointer', padding: 2 }}
                    />
                    <Input
                      value={config.bgColor || '#0f172a'}
                      onChange={e => updateConfig({ bgColor: e.target.value })}
                      style={{ width: 120, fontFamily: 'monospace' }}
                      maxLength={7}
                    />
                    <div style={{
                      width: 60, height: 36, borderRadius: 6,
                      background: config.bgColor || '#0f172a',
                      border: '1px solid #e2e8f0',
                    }} />
                  </Space>
                </Form.Item>
              )}

              {config.bgType === 'image' && (
                <Form.Item label="Background Image">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Upload accept="image/*" showUploadList={false} beforeUpload={handleBgImageUpload}>
                      <Button icon={<PictureOutlined />}>Choose Background Image</Button>
                    </Upload>
                    {bgImgFile && <Tag color="green">{bgImgFile.name}</Tag>}
                    {config.bgImageUrl && (
                      <div style={{
                        width: '100%', height: 80, borderRadius: 6, overflow: 'hidden',
                        border: '1px solid #e2e8f0',
                        backgroundImage: `url(${config.bgImageUrl})`,
                        backgroundSize: 'cover', backgroundPosition: 'center',
                      }} />
                    )}
                    <Text style={{ fontSize: 11, color: '#94a3b8' }}>
                      Tip: Use a dark image for best text contrast.
                    </Text>
                  </Space>
                </Form.Item>
              )}
            </Card>
          </Col>

          {/* Logo & Branding */}
          <Col xs={24} lg={12}>
            <Card title={<SectionHeader title="Logo & Branding" icon="🏷️" />} style={{ borderRadius: 10 }}>
              <Form.Item label="Upload Logo">
                <Space direction="vertical" size={10} style={{ width: '100%' }}>
                  <Upload accept="image/*" showUploadList={false} beforeUpload={handleLogoUpload}>
                    <Button icon={<UploadOutlined />}>Choose Logo File</Button>
                  </Upload>
                  {config.logoUrl && (
                    <div style={{ border: '1px dashed #d1d5db', borderRadius: 6, padding: 8, display: 'inline-block', background: '#f8fafc' }}>
                      <img src={config.logoUrl} alt="Logo preview"
                        style={{ maxHeight: 60, maxWidth: 200, objectFit: 'contain' }} />
                    </div>
                  )}
                  {logoFile && <Tag color="green">{logoFile.name}</Tag>}
                </Space>
              </Form.Item>
              <Form.Item label="Logo Placement" style={{ marginBottom: 0 }}>
                <Radio.Group value={config.logoPlacement} onChange={e => updateConfig({ logoPlacement: e.target.value })} buttonStyle="solid">
                  <Radio.Button value="left">Left</Radio.Button>
                  <Radio.Button value="center">Center</Radio.Button>
                  <Radio.Button value="right">Right</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Card>
          </Col>

          {/* Clock */}
          <Col xs={24} lg={12}>
            <Card title={<SectionHeader title="Date & Time Clock" icon={<ClockCircleOutlined />} />} style={{ borderRadius: 10 }}>
              <Form.Item label="Show Live Clock">
                <Switch
                  checked={config.showClock}
                  onChange={v => updateConfig({ showClock: v })}
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                />
              </Form.Item>

              {config.showClock && (
                <>
                  <Form.Item label="Clock Position">
                    <Select
                      value={config.clockPosition}
                      onChange={v => updateConfig({ clockPosition: v })}
                      options={CLOCK_POSITIONS}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Form.Item label="Time Format" style={{ marginBottom: 0 }}>
                    <Radio.Group
                      value={config.clockFormat}
                      onChange={e => updateConfig({ clockFormat: e.target.value })}
                      buttonStyle="solid"
                    >
                      <Radio.Button value="24h">24-hour</Radio.Button>
                      <Radio.Button value="12h">12-hour (AM/PM)</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </>
              )}
            </Card>
          </Col>

          {/* Template Selection */}
          <Col xs={24}>
            <Card title={<SectionHeader title="Template Selection" icon="🎨" />} style={{ borderRadius: 10 }}>
              <Row gutter={[16, 16]}>
                {TEMPLATES.map(({ key, label, desc, Preview }) => {
                  const selected = config.template === key;
                  return (
                    <Col xs={24} sm={8} key={key}>
                      <Card
                        hoverable
                        onClick={() => updateConfig({ template: key })}
                        style={{
                          borderRadius: 8,
                          border: selected ? `2px solid ${PRIMARY}` : '2px solid #e2e8f0',
                          cursor: 'pointer', position: 'relative',
                          transition: 'border-color 0.2s, box-shadow 0.2s',
                          boxShadow: selected ? `0 0 0 3px ${PRIMARY}22` : 'none',
                        }}
                        bodyStyle={{ padding: 14 }}
                      >
                        {selected && (
                          <div style={{
                            position: 'absolute', top: 8, right: 8,
                            width: 22, height: 22, borderRadius: '50%',
                            background: PRIMARY, display: 'flex',
                            alignItems: 'center', justifyContent: 'center', zIndex: 1,
                          }}>
                            <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>✓</span>
                          </div>
                        )}
                        <Preview />
                        <div style={{ marginTop: 10 }}>
                          <Text strong style={{ fontSize: 13, color: DARK }}>{label}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 11 }}>{desc}</Text>
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Card>
          </Col>

          {/* Fields to Display + Order */}
          <Col xs={24} lg={12}>
            <Card title={<SectionHeader title="Fields to Display" icon="📋" />} style={{ borderRadius: 10 }}>
              <Form.Item label="Select fields to show:">
                <Checkbox.Group
                  value={config.fields}
                  onChange={vals => updateConfig({ fields: vals })}
                  style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                  {Object.entries(FIELD_LABELS).map(([val, lbl]) => (
                    <Checkbox key={val} value={val}>{lbl}</Checkbox>
                  ))}
                </Checkbox.Group>
              </Form.Item>
            </Card>
          </Col>

          {/* Field Order (position) */}
          <Col xs={24} lg={12}>
            <Card title={<SectionHeader title="Field Display Order & Position" icon="↕️" />} style={{ borderRadius: 10 }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
                Drag or use arrows to reorder how fields appear on screen (top → bottom).
              </Text>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {fieldOrder.map((field, idx) => {
                  const isEnabled = config.fields.includes(field);
                  return (
                    <div key={field} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 12px', borderRadius: 8,
                      background: isEnabled ? '#f0f7ff' : '#f8fafc',
                      border: isEnabled ? '1px solid #bfdbfe' : '1px solid #e2e8f0',
                      opacity: isEnabled ? 1 : 0.5,
                    }}>
                      <span style={{ fontSize: 16, width: 22, textAlign: 'center' }}>
                        {field === 'photo' ? '📷'
                          : field === 'name' ? '👤'
                          : field === 'category' ? '🏷️'
                          : field === 'company' ? '🏢'
                          : field === 'location' ? '📍'
                          : '💬'}
                      </span>
                      <Text style={{ flex: 1, fontSize: 13, fontWeight: isEnabled ? 600 : 400 }}>
                        {FIELD_LABELS[field]}
                      </Text>
                      <Space size={4}>
                        <Button size="small" icon={<ArrowUpOutlined />}
                          disabled={idx === 0}
                          onClick={() => moveField(idx, -1)}
                          style={{ width: 28, height: 28 }}
                        />
                        <Button size="small" icon={<ArrowDownOutlined />}
                          disabled={idx === fieldOrder.length - 1}
                          onClick={() => moveField(idx, 1)}
                          style={{ width: 28, height: 28 }}
                        />
                      </Space>
                    </div>
                  );
                })}
              </div>
              <Text type="secondary" style={{ fontSize: 11, marginTop: 10, display: 'block' }}>
                Greyed-out fields are disabled — enable them in "Fields to Display" above.
              </Text>
            </Card>
          </Col>

          {/* Welcome Message & Duration */}
          <Col xs={24} lg={12}>
            <Card title={<SectionHeader title="Welcome Message" icon="💬" />} style={{ borderRadius: 10 }}>
              <Form.Item label="Custom Message" extra="Shown on the welcome screen.">
                <Input
                  size="large"
                  placeholder="Welcome to the Event!"
                  value={config.welcomeMessage}
                  onChange={e => updateConfig({ welcomeMessage: e.target.value })}
                  maxLength={120}
                  showCount
                />
              </Form.Item>
              <Form.Item
                label={
                  <Space>
                    <span>Display Duration</span>
                    <Tag color="blue">{config.displayDuration}s</Tag>
                  </Space>
                }
                extra="How long the delegate card stays on screen."
                style={{ marginBottom: 0 }}
              >
                <Slider
                  min={3} max={15} step={1}
                  value={config.displayDuration}
                  onChange={val => updateConfig({ displayDuration: val })}
                  marks={{ 3: '3s', 5: '5s', 8: '8s', 10: '10s', 15: '15s' }}
                  tooltip={{ formatter: v => `${v} sec` }}
                />
              </Form.Item>
            </Card>
          </Col>

          {/* Actions */}
          <Col xs={24}>
            <Card style={{ borderRadius: 10 }}>
              <Space size={12} wrap>
                <Button type="primary" size="large" icon={<SaveOutlined />} onClick={handleSave}
                  style={{ background: PRIMARY, borderColor: PRIMARY, minWidth: 160 }}>
                  Save Configuration
                </Button>
                <Button size="large" icon={<EyeOutlined />} onClick={() => navigate('/welcome-display')}
                  style={{ minWidth: 190 }}>
                  Open Display Screen
                </Button>
              </Space>
              <div style={{ marginTop: 10 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Configuration is stored in browser localStorage. Each display screen (TV/laptop) can independently select its own location.
                </Text>
              </div>
            </Card>
          </Col>

        </Row>
      </Form>
    </div>
  );
}
